/* `/overview` — the workspace's runs, spend, tokens, tool calls and failures for the
   Overview page. `backend/modules/overview/README.md` for the wire.

   Live without a poll: every run's start and end reaches the whole org on the events
   socket, and `EVENT_MAP` (`events.ts`) re-reads this query on each. */
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";

import type { ApiOverview } from "@/shared/types";

export const overviewKeys = {
  all: ["overview"] as const,
  range: (days: number, tz: string) => [...overviewKeys.all, days, tz] as const,
};

/** Days are the viewer's: the server buckets runs in this zone. */
const VIEWER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";

export async function getOverview(days: number, tz: string): Promise<ApiOverview> {
  return unwrap(await client.get<Envelope<ApiOverview>>("/overview", { params: { days, tz } }));
}

/** Switching the range keeps the last one on screen until the new one arrives. */
export const useOverview = (days: number) =>
  useQuery({
    queryKey: overviewKeys.range(days, VIEWER_TZ),
    queryFn: () => getOverview(days, VIEWER_TZ),
    placeholderData: keepPreviousData,
  });
